import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { 
  BrowserCacheLocation, 
  InteractionType, 
  IPublicClientApplication, 
  LogLevel, 
  PublicClientApplication 
} from '@azure/msal-browser';
import { environment } from '../../environments/environment';

/**
 * ========================================
 * AUTHENTICATION MODE SELECTION
 * ========================================
 * Toggle between Azure AD B2C and Azure AD B2B by commenting/uncommenting the sections below
 * 
 * CURRENT MODE: B2B (Business-to-Business)
 * 
 * To switch back to B2C:
 * 1. Comment out the "ACTIVE: B2B" section below
 * 2. Uncomment the "DISABLED: B2C" section below
 * 3. Rebuild the application (npm run build)
 */

// ========================================
// DISABLED: Azure AD B2C Configuration
// ========================================
// Uncomment this block to use B2C authentication
/*
const AUTH_CONFIG = environment.b2c;
const API_SCOPES = environment.b2c.scopes;
*/

// ========================================
// ACTIVE: Azure AD B2B Configuration
// ========================================
// Comment out this block to disable B2B and use B2C instead
const AUTH_CONFIG = environment.b2b;
const API_SCOPES = environment.b2b.scopes;

/**
 * Redirect URIs - resolve from current host to avoid localhost fallback in deployed envs
 * Falls back to environment.urls if needed
 */
function getRedirectUri(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/`;
  }

  // Build-time fallback from environment
  return environment.urls.redirectUri || 'https://msuitedocsilaqc.z29.web.core.windows.net/docs/oauth2-redirect';
}

function getPostLogoutRedirectUri(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/`;
  }

  // Build-time fallback from environment
  return environment.urls.postLogoutRedirectUri || 'https://msuitedocsilaqc.z29.web.core.windows.net/';
}

/**
 * MSAL Logger callback for debugging
 */
function loggerCallback(logLevel: LogLevel, message: string): void {
  if (logLevel === LogLevel.Error) {
    console.error('🔐 MSAL Error:', message);
  } else if (logLevel === LogLevel.Warning) {
    console.warn('🔐 MSAL Warning:', message);
  } else if (logLevel === LogLevel.Info) {
    console.log('🔐 MSAL Info:', message);
  }
}

/**
 * MSAL Configuration for Azure AD Authentication
 * Values are imported from environment configuration based on AUTH_CONFIG selection above
 */
export const msalConfig = {
  auth: {
    clientId: AUTH_CONFIG.clientId,
    authority: AUTH_CONFIG.authority,
    knownAuthorities: AUTH_CONFIG.knownAuthorities,
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: true  // Enable cookies for better reliability
  },
  system: {
    loggerOptions: {
      loggerCallback,
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false
    },
    allowNativeBroker: false
  }
};

/**
 * Factory function to create MSAL instance
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Guard Configuration
 * Defines how the guard handles authentication
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: API_SCOPES,
      redirectUri: getRedirectUri()
    },
    loginFailedRoute: '/login'
  };
}

/**
 * MSAL Interceptor Configuration
 * Defines which API endpoints require authentication
 * Uses environment.apiBaseUrl so it automatically points to the correct environment
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  
  // Use environment.apiBaseUrl so it automatically routes to correct environment
  // MSAL matches URLs that start with this base URL
  protectedResourceMap.set(environment.apiBaseUrl, API_SCOPES);
  
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

/**
 * Export scopes for use in components
 */
export const loginRequest = {
  scopes: API_SCOPES
};
